import { useTranslation } from 'react-i18next';

import { Box, ListItemText, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import moment from 'moment';

import { currency } from 'lib/helpers';
import {
	NfcCardTransaction,
	getTransactionActor,
	getTransactionDate,
	getTransactionOrderId,
	getTransactionSign,
} from 'lib/models/nfcCards';

import useIsMobile from 'hooks/useIsMobile';
import useNfcCardLabels from 'hooks/useNfcCardLabels';

import ResponsiveTable from 'components/ResponsiveTable';
import WrappingPagination from 'components/ResponsiveTable/WrappingPagination';

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

type Pagination = {
	count: number;
	page: number;
	rowsPerPage: number;
	rowsPerPageOptions: number[];
	onPageChange: (event: unknown, page: number) => void;
	onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

type Props = {
	/** The card's shadow username, so the card itself is never shown as who made a movement. */
	cardUsername?: string | null;
	transactions: NfcCardTransaction[];
	pagination: Pagination;
};

/**
 * The movements of the balance of a card: what kind, how much (signed), the balance it left
 * (as the API stored it), the order it belongs to, who made it and when. The shadow user
 * never shows up here: the rows are read without it and the actor is checked against it
 * (see `getTransactionActor`).
 */
export default function NfcCardTransactionsTable({ cardUsername, transactions, pagination }: Props) {
	const { t } = useTranslation();
	const isMobile = useIsMobile();
	const { transactionTypeLabel } = useNfcCardLabels();

	const amount = (row: NfcCardTransaction) => {
		const sign = getTransactionSign(row);
		const value = sign === null ? row.amount : sign * row.amount;

		return (
			<Typography variant="body2" color={sign === null ? 'text.primary' : sign < 0 ? 'error.main' : 'success.main'}>
				{value > 0 && sign !== null ? '+' : ''}
				{currency(value)}
			</Typography>
		);
	};

	// Rows written before the API stored it have no balance after them, and none is made up
	const balanceAfter = (row: NfcCardTransaction) =>
		row.balanceAfter === null || row.balanceAfter === undefined ? '—' : currency(row.balanceAfter);

	const orderId = (row: NfcCardTransaction) => {
		const id = getTransactionOrderId(row);
		return id ? (
			<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
				{id}
			</Typography>
		) : (
			'-'
		);
	};

	// A charge at the bar records nobody on the row, so it is said to be the bar's
	const actor = (row: NfcCardTransaction) =>
		getTransactionActor(row, cardUsername) ?? (row.status === 'order' ? t('nfc:transactionBar') : '-');

	const date = (row: NfcCardTransaction) => {
		const value = getTransactionDate(row);
		return value ? moment(value).format(DATE_TIME_FORMAT) : '-';
	};

	return (
		<>
			<ResponsiveTable
				elements={transactions}
				tableSx={{ maxHeight: '65vh' }}
				list={{
					primaryKey: '_id',
					renderListItemText: (row) => (
						<ListItemText
							primary={
								<Box>
									<Typography>{transactionTypeLabel(row.status)}</Typography>
									{amount(row)}
									<Typography variant="body2">
										{t('nfc:transactionBalanceAfter')}: {balanceAfter(row)}
									</Typography>
								</Box>
							}
							secondary={
								<Box component="span">
									{!!getTransactionOrderId(row) && (
										<Typography variant="caption" component="div">
											{t('nfc:transactionOrderId')}:{' '}
											<Box component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
												{getTransactionOrderId(row)}
											</Box>
										</Typography>
									)}
									<Typography variant="caption" component="div">
										{actor(row)} - {date(row)}
									</Typography>
								</Box>
							}
						/>
					),
				}}
				table={{
					renderHead: () => (
						<TableHead>
							<TableRow>
								<TableCell align="left" padding="normal">
									{t('nfc:transactionType')}
								</TableCell>
								<TableCell align="right" padding="normal">
									{t('nfc:transactionAmount')}
								</TableCell>
								<TableCell align="right" padding="normal">
									{t('nfc:transactionBalanceAfter')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('nfc:transactionOrderId')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('nfc:transactionBy')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('nfc:transactionDate')}
								</TableCell>
							</TableRow>
						</TableHead>
					),
					renderRow: (row) => (
						<TableRow tabIndex={-1} key={row._id}>
							<TableCell>{transactionTypeLabel(row.status)}</TableCell>
							<TableCell align="right">{amount(row)}</TableCell>
							<TableCell align="right">{balanceAfter(row)}</TableCell>
							<TableCell>{orderId(row)}</TableCell>
							<TableCell>{actor(row)}</TableCell>
							<TableCell>{date(row)}</TableCell>
						</TableRow>
					),
					pagination,
				}}
			/>

			{/* The list ResponsiveTable renders on mobile has no pagination of its own */}
			{isMobile && <WrappingPagination {...pagination} />}
		</>
	);
}
